import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Alert } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../components/constants";
import { useAuth } from "../components/contexts/AuthContext";
import AuthShell, { primaryButtonStyle } from "../features/auth/views/AuthShell";
import ForgotPasswordForm from "../features/auth/views/ForgotPasswordForm";
import { PASSWORD_RESET_ENABLED } from "../features/auth/models/passwordResetModel";
import {
  describeClientDevice,
  getOrCreateClientDeviceId,
  persistClientDeviceId,
} from "../components/auth/clientDevice";

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);       // { type: 'error' | 'warning', text }
  const [forgot, setForgot] = useState(false);
  // "Forgot password?" appears only after two wrong passwords in a row.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const showForgot = PASSWORD_RESET_ENABLED && failedAttempts >= 2;

  const onFinish = async ({ username, password }) => {
    setError(null);
    setLoading(true);
    try {
      const clientDeviceId = getOrCreateClientDeviceId();
      const res = await axios.post(`${API_DOMAIN}/auth/login`, {
        username: username.trim(),
        password,
        client_device_id: clientDeviceId,
        ...describeClientDevice(),
      });
      if (res.data.error) {
        if (res.data.error === "approval") {
          setError({ type: "warning", text: "Your account is awaiting admin approval. You'll be able to sign in once it's activated." });
        } else {
          setError({ type: "error", text: typeof res.data.error === "string" ? res.data.error : "Invalid credentials." });
          setFailedAttempts(count => count + 1);
        }
        return;
      }
      persistClientDeviceId(res.data.client_device_id || clientDeviceId);
      const responseUser = res.data.user || res.data;
      login({
        id: res.data.id,
        token: res.data.token,
        session_id: res.data.session_id,
        device_id: res.data.device_id,
        client_device_id: res.data.client_device_id || clientDeviceId,
        user: {
          username: responseUser.username,
          name: responseUser.name,
          email: responseUser.email,
          user_type: responseUser.user_type,
          admin: responseUser.admin,
          wati_id: responseUser.wati_id,
          pfp: responseUser.pfp,
          internal_chat_enabled: responseUser.internal_chat_enabled ?? res.data.internal_chat_enabled,
          internal_chat_last_seen_at: responseUser.internal_chat_last_seen_at ?? res.data.internal_chat_last_seen_at,
        },
      });
      navigate("/", { replace: true });
    } catch (requestError) {
      const response = requestError?.response;
      if (response?.status === 403 && response?.data?.data?.code === "DEVICE_LOGIN_NOT_ALLOWED") {
        setError({
          type: "error",
          text: response.data.message || "This device is not authorized to sign in. Ask an administrator to allow it.",
        });
      } else {
        setError({ type: "error", text: "Could not reach the server. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (forgot) {
    return <AuthShell subtitle="Reset your password"><ForgotPasswordForm onBack={() => setForgot(false)} /></AuthShell>;
  }

  return (
    <AuthShell subtitle="Sign in to your workspace">
      {error && (
        <Alert
          type={error.type}
          message={error.text}
          showIcon
          style={{ marginBottom: 18, borderRadius: 10 }}
        />
      )}

      <Form layout="vertical" onFinish={onFinish} requiredMark={false} className="login-card-input">
        <Form.Item
          name="username"
          rules={[{ required: true, message: "Enter your username" }, { min: 3, message: "At least 3 characters" }]}
          style={{ marginBottom: 16 }}
        >
          <Input
            size="large"
            prefix={<UserOutlined />}
            placeholder="Username"
            autoComplete="username"
            autoFocus
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, message: "Enter your password" }, { min: 4, message: "At least 4 characters" }]}
          style={{ marginBottom: showForgot ? 8 : 22 }}
        >
          <Input.Password
            size="large"
            prefix={<LockOutlined />}
            placeholder="Password"
            autoComplete="current-password"
          />
        </Form.Item>
        {showForgot && (
          <div style={{ textAlign: "right", marginBottom: 16 }}>
            <Button type="link" className="login-link" onClick={() => { setError(null); setForgot(true); }}>Forgot password?</Button>
          </div>
        )}
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={loading}
          className="login-signin-btn"
          style={primaryButtonStyle}
        >
          Sign In
        </Button>
      </Form>
    </AuthShell>
  );
}

export default Login;
